import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import {
  getDetailSession,
  getSeniorDetailSession,
  getSeniorBuddyDetailSession,
  verifyJoinSession,
  getDeeplinkGenToken,
  checkJoinAvailability,
} from 'redux/actions/mvp/session.action';
import { I18n } from 'helpers/i18n';
import { isMvpUserGuide } from 'helpers/mvpAuth';
import AiSupportSessionVideo from '../ai-support-session-video';
import {
  evaluateJoinEligibility,
  buildJoinRejectionToastLines,
  canProceedToVerifyJoin,
} from './sessionJoinRules';
import KambriaMeetingDownloadModal from './KambriaMeetingDownloadModal';
import {
  MEETING_JOIN_DEEPLINK_TOAST_ID,
  startDeeplinkOpenAppToast,
} from './deeplinkOpenAppToast';
import {
  WORKPLACE_DEEPLINK_JOIN_BASE,
  WORKPLACE_TERMS_URL_PLACEHOLDER,
  WORKPLACE_PRIVACY_URL_PLACEHOLDER,
} from './workplaceMeetingConfig';
import { loadPrejoinMediaPrefs } from 'constants/mvpDeviceCombos';
import './meeting-join.scss';

const toastErrorOpts = {
  position: 'top-center',
  autoClose: 5000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: false,
  draggable: false,
  theme: 'colored',
  className: 'toast-error-container toast-error-container-after',
};

function pushJoinRejectionToast(sessionInfo, evaluation) {
  const { title, body } = buildJoinRejectionToastLines(I18n, sessionInfo, evaluation);
  toast.error(`${title} ${body}`, toastErrorOpts);
}

function pickJoinAvailabilityMessage(res) {
  return typeof res?.message === 'string' && res.message.trim() ? res.message.trim() : '';
}

function MeetingJoinPage({
  history,
  match,
  user,
  header,
  footer,
  getDetailSession,
  verifyJoinSession,
  getDeeplinkGenToken,
  checkJoinAvailability,
}) {
  const sessionId = match.params.id;
  const [sessionInfo, setSessionInfo] = useState(null);
  const [loadState, setLoadState] = useState('loading');
  const [showBrowserPrep, setShowBrowserPrep] = useState(true);
  const [workplaceLoading, setWorkplaceLoading] = useState(false);
  const [joinBrowserLoading, setJoinBrowserLoading] = useState(false);
  const [showAppDownloadModal, setShowAppDownloadModal] = useState(false);
  const deeplinkToastCleanupRef = useRef(null);

  const loadSessionDetailAndRatingData = useCallback(async () => {
    const sessionDetail = await getDetailSession({ sessionId, notAddState: true });
    if (!sessionDetail) {
      return null;
    }
    const isGuide = isMvpUserGuide(user);
    const apiCall = isGuide ? getSeniorBuddyDetailSession : getSeniorDetailSession;
    const ratingData = await apiCall({ id: sessionId, notAddState: true });
    return {
      ...sessionDetail,
      needToEvaluate: ratingData?.needToEvaluate,
      dataRating: ratingData?.dataRating,
      dataEvaluateSenior: ratingData?.dataEvaluateSenior,
      dataEvaluateBuddy: ratingData?.dataEvaluateBuddy,
      dataRatingTopic: ratingData?.dataRatingTopic,
    };
  }, [getDetailSession, sessionId, user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadState('loading');
      try {
        const data = await loadSessionDetailAndRatingData();
        if (cancelled) return;
        if (!data) {
          setLoadState('error');
          return;
        }
        if (!data.sessionUserId && !data._id) {
          setLoadState('error');
          return;
        }
        if (I18n.language === 'en') {
          data.title = data.titleEn || data.title;
        }
        setSessionInfo(data);
        setLoadState('ready');
      } catch {
        if (!cancelled) setLoadState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadSessionDetailAndRatingData]);

  // Prep giống session-detail: sync lại phiên khi vào màn + khi tab active (state/infoExtra/start-end)
  useEffect(() => {
    if (!showBrowserPrep || loadState !== 'ready') return;

    const sync = () => {
      loadSessionDetailAndRatingData().then((data) => {
        if (!data) return;
        const next = { ...data };
        if (I18n.language === 'en') {
          next.title = next.titleEn || next.title;
        }
        setSessionInfo(next);
      });
    };

    sync();
    const onVis = () => {
      if (document.visibilityState === 'visible') sync();
    };
    document.addEventListener('visibilitychange', onVis);
    const intervalId = window.setInterval(sync, 60000);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.clearInterval(intervalId);
    };
  }, [showBrowserPrep, loadState, loadSessionDetailAndRatingData]);

  useEffect(() => {
    return () => {
      if (deeplinkToastCleanupRef.current) {
        deeplinkToastCleanupRef.current();
        deeplinkToastCleanupRef.current = null;
      }
      toast.dismiss(MEETING_JOIN_DEEPLINK_TOAST_ID);
    };
  }, []);

  const onJoinWorkplaceApp = async () => {
    if (!sessionInfo || workplaceLoading) return;
    setWorkplaceLoading(true);
    try {
      const latestSessionInfo = await loadSessionDetailAndRatingData();
      if (!latestSessionInfo) {
        toast.error(I18n.t('meeting_join_deeplink_error'), toastErrorOpts);
        return;
      }
      if (I18n.language === 'en') {
        latestSessionInfo.title = latestSessionInfo.titleEn || latestSessionInfo.title;
      }
      setSessionInfo(latestSessionInfo);

      const ev = evaluateJoinEligibility(latestSessionInfo);
      if (!ev.ok) {
        pushJoinRejectionToast(latestSessionInfo, ev);
        return;
      }
      const resCheckJoinAvailability = await checkJoinAvailability({
        sessionId: latestSessionInfo.sessionUserId,
        sessionUserId: latestSessionInfo.sessionUserId,
      });
      if (!resCheckJoinAvailability || resCheckJoinAvailability.status !== 'OK') {
        const apiMsg = pickJoinAvailabilityMessage(resCheckJoinAvailability);
        toast.error(apiMsg || I18n.t('unable_join_session'), toastErrorOpts);
        return;
      }

      const media = loadPrejoinMediaPrefs(sessionId) || { audio: true, video: true };
      const res = await getDeeplinkGenToken({
        id: sessionId,
        audio: media.audio !== false,
        video: media.video !== false,
      });

      if (!res?.token) {
        const apiMsg = typeof res?.error === 'string' ? res.error.trim() : '';
        toast.error(apiMsg || I18n.t('meeting_join_deeplink_error'), toastErrorOpts);
        return;
      }

      const url = `${WORKPLACE_DEEPLINK_JOIN_BASE}${encodeURIComponent(res.token)}`;

      if (deeplinkToastCleanupRef.current) {
        deeplinkToastCleanupRef.current();
        deeplinkToastCleanupRef.current = null;
      }

      deeplinkToastCleanupRef.current = startDeeplinkOpenAppToast(() => {
        setShowAppDownloadModal(true);
      });

      const a = document.createElement('a');
      a.href = url;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setWorkplaceLoading(false);
    }
  };

  const onJoinBrowser = async () => {
    if (!sessionInfo || joinBrowserLoading) return;
    setJoinBrowserLoading(true);
    try {
      const latestSessionInfo = await loadSessionDetailAndRatingData();
      if (!latestSessionInfo) {
        toast.error(I18n.t('unable_join_session'), toastErrorOpts);
        return;
      }
      if (I18n.language === 'en') {
        latestSessionInfo.title = latestSessionInfo.titleEn || latestSessionInfo.title;
      }
      setSessionInfo(latestSessionInfo);

      const ev = evaluateJoinEligibility(latestSessionInfo);
      if (!ev.ok) {
        pushJoinRejectionToast(latestSessionInfo, ev);
        return;
      }
      const resCheckJoinAvailability = await checkJoinAvailability({
        sessionId: latestSessionInfo.sessionUserId,
        sessionUserId: latestSessionInfo.sessionUserId,
      });
      if (!resCheckJoinAvailability || resCheckJoinAvailability.status !== 'OK') {
        const apiMsg = pickJoinAvailabilityMessage(resCheckJoinAvailability);
        toast.error(apiMsg || I18n.t('unable_join_session'), toastErrorOpts);
        return;
      }

      if (!canProceedToVerifyJoin(latestSessionInfo)) {
        toast.error(I18n.t('unable_join_session'), toastErrorOpts);
        return;
      }

      const verifyData = await verifyJoinSession({
        sessionId: latestSessionInfo.sessionUserId,
        sessionUserId: latestSessionInfo.sessionUserId,
      });

      if (!verifyData || verifyData.status !== 'OK') {
        toast.error(I18n.t('unable_join_session'), toastErrorOpts);
        return;
      }

      history.push(`/session-video/${sessionId}`);
    } finally {
      setJoinBrowserLoading(false);
    }
  };

  const joinAfterBrowserPrep = () => {
    if (!sessionInfo) return;

    const ev = evaluateJoinEligibility(sessionInfo);
    if (!ev.ok) {
      pushJoinRejectionToast(sessionInfo, ev);
      return;
    }

    setShowBrowserPrep(false);
  };

  const openAppDownloadModalManual = useCallback(() => {
    setShowAppDownloadModal(true);
  }, []);

  const closeAppDownloadModal = useCallback(() => {
    setShowAppDownloadModal(false);
  }, []);

  /* Cùng layout site như session-detail: header app + nội dung + footer Follow Us */
  if (loadState === 'loading') {
    return (
      <div id="site_wrapper" className="meeting-join-page">
        <div>{header}</div>
        <main className="main meeting-join-page__main">
          <div className="meeting-join">
            <div className="meeting-join__loading">{I18n.t('loading')}</div>
          </div>
        </main>
        {footer}
      </div>
    );
  }

  if (loadState === 'error' || !sessionInfo) {
    return (
      <div id="site_wrapper" className="meeting-join-page">
        <div>{header}</div>
        <main className="main meeting-join-page__main">
          <div className="meeting-join">
            <div className="meeting-join__error">
              <p>{I18n.t('meeting_join_load_error')}</p>
              <Link to={`/session-detail/${sessionId}`}>{I18n.t('meeting_join_back_to_detail')}</Link>
            </div>
          </div>
        </main>
        {footer}
      </div>
    );
  }

  if (showBrowserPrep) {
    return (
      <div id="site_wrapper" className="ai-support-session-page">
        <div className="ai-support-session-page__viewport">
          <div>{header}</div>
          <AiSupportSessionVideo
            sessionInfo={sessionInfo}
            sessionId={sessionId}
            joinSession={joinAfterBrowserPrep}
            goBack={() => history.push(`/session-detail/${sessionId}`)}
          />
        </div>
        {footer}
      </div>
    );
  }

  return (
    <div id="site_wrapper" className="meeting-join-page">
      <div>{header}</div>
      <main className="main meeting-join-page__main">
        <div className="meeting-join">
          <div className="meeting-join__wrap">
            {/* Tạm ẩn topic (tiêu đề phiên sessionInfo.title)
            {sessionInfo?.title ? (
              <h1 className="meeting-join__title meeting-join__title--session">{sessionInfo.title}</h1>
            ) : null}
            */}

            <div className="meeting-join__intro">
              <p className="meeting-join__intro-line">{I18n.t('meeting_join_intro_line_1')}</p>
              <p className="meeting-join__intro-line">
                {I18n.t('meeting_join_intro_line_2_before')}
                <strong>{I18n.t('meeting_join_intro_line_2_bold')}</strong>
                {I18n.t('meeting_join_intro_line_2_after')}
              </p>
            </div>

            <div className="meeting-join__actions">
              <button
                type="button"
                className="meeting-join__btn-primary"
                disabled={workplaceLoading}
                onClick={onJoinWorkplaceApp}
              >
                {workplaceLoading ? I18n.t('loading') : I18n.t('meeting_join_button_workplace')}
              </button>
              <button
                type="button"
                className="meeting-join__btn-secondary"
                disabled={joinBrowserLoading}
                onClick={onJoinBrowser}
              >
                {joinBrowserLoading ? I18n.t('loading') : I18n.t('meeting_join_button_browser')}
              </button>
            </div>

            <p className="meeting-join__bottom-download">
              {I18n.t('meeting_join_bottom_hint')}
              <button
                type="button"
                className="meeting-join__bottom-download-link"
                onClick={openAppDownloadModalManual}
              >
                {I18n.t('meeting_join_bottom_link')}
              </button>
            </p>

            <div className="meeting-join__legal">
              <a href={WORKPLACE_TERMS_URL_PLACEHOLDER} target="_blank" rel="noopener noreferrer">
                {I18n.t('meeting_join_terms')}
              </a>
              <span aria-hidden>|</span>
              <a href={WORKPLACE_PRIVACY_URL_PLACEHOLDER} target="_blank" rel="noopener noreferrer">
                {I18n.t('meeting_join_privacy')}
              </a>
            </div>
          </div>
        </div>
      </main>
      {footer}

      <KambriaMeetingDownloadModal show={showAppDownloadModal} onHide={closeAppDownloadModal} />
    </div>
  );
}

const mapStateToProps = ({ user }) => ({ user });

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getDetailSession,
      verifyJoinSession,
      getDeeplinkGenToken,
      checkJoinAvailability,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(MeetingJoinPage);
